<template>
  <Teleport to="body">
    <motion.div
      v-if="modelValue"
      class="plan-upgrade-overlay"
      :initial="{ opacity: 0 }"
      :animate="{ opacity: 1 }"
      @click.self="close"
    >
      <motion.div
        class="plan-upgrade-card"
        :initial="{ opacity: 0, scale: 0.92, y: 24 }"
        :animate="{ opacity: 1, scale: 1, y: 0 }"
        :transition="{ type: 'spring', stiffness: 320, damping: 28 }"
      >
        <motion.div
          class="plan-upgrade-glow"
          :animate="{ opacity: [0.4, 0.85, 0.4] }"
          :transition="{ duration: 3, repeat: Infinity, ease: 'easeInOut' }"
        />
        <div class="plan-upgrade-icon-wrap">
          <svg class="plan-upgrade-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
          </svg>
        </div>
        <h2 class="plan-upgrade-title">{{ displayTitle }}</h2>
        <p class="plan-upgrade-desc">{{ displayDescription }}</p>
        <p v-if="displayPlanoHint" class="plan-upgrade-hint">{{ displayPlanoHint }}</p>
        <motion.button
          type="button"
          class="plan-upgrade-cta"
          :whileHover="{ scale: 1.02 }"
          :whileTap="{ scale: 0.98 }"
          @click="goUpgrade"
        >
          {{ displayCtaLabel }}
        </motion.button>
        <button type="button" class="plan-upgrade-dismiss" @click="close">{{ t('planGate.dismiss') }}</button>
      </motion.div>
    </motion.div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { motion } from 'motion-v'

const { t } = useI18n()

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  planoHint: { type: String, default: '' },
  ctaLabel: { type: String, default: '' }
})

const displayTitle = computed(() => props.title || t('planGate.upgradeTitle'))
const displayDescription = computed(() => props.description || t('planGate.upgradeDescription'))
const displayPlanoHint = computed(() => props.planoHint || t('planGate.upgradeHint'))
const displayCtaLabel = computed(() => props.ctaLabel || t('planGate.upgradeCta'))

const emit = defineEmits(['update:modelValue'])
const router = useRouter()

const close = () => emit('update:modelValue', false)

const goUpgrade = async () => {
  close()
  await router.push('/upgrade')
}
</script>

<style scoped>
.plan-upgrade-overlay {
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(8px);
}
.plan-upgrade-card {
  position: relative;
  overflow: hidden;
  width: min(440px, 100%);
  padding: 32px 28px 24px;
  border-radius: 20px;
  border: 1px solid color-mix(in srgb, var(--accent-primary) 45%, var(--border-primary));
  background: linear-gradient(165deg, var(--bg-secondary), var(--bg-primary));
  box-shadow: 0 32px 64px rgba(15, 23, 42, 0.35);
  text-align: center;
}
.plan-upgrade-glow {
  position: absolute;
  inset: -40%;
  background: radial-gradient(circle, color-mix(in srgb, var(--accent-primary) 35%, transparent), transparent 55%);
  pointer-events: none;
}
.plan-upgrade-icon-wrap {
  position: relative;
  z-index: 1;
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--accent-primary) 22%, transparent);
  border: 1px solid color-mix(in srgb, var(--accent-primary) 50%, var(--border-primary));
}
.plan-upgrade-icon {
  width: 28px;
  height: 28px;
  color: var(--accent-primary);
}
.plan-upgrade-title {
  position: relative;
  z-index: 1;
  margin: 0 0 10px;
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--text-primary);
}
.plan-upgrade-desc {
  position: relative;
  z-index: 1;
  margin: 0 0 8px;
  font-size: 15px;
  line-height: 1.5;
  color: var(--text-secondary);
}
.plan-upgrade-hint {
  position: relative;
  z-index: 1;
  margin: 0 0 20px;
  font-size: 13px;
  color: color-mix(in srgb, var(--accent-primary) 70%, var(--text-secondary));
}
.plan-upgrade-cta {
  position: relative;
  z-index: 1;
  width: 100%;
  border: none;
  border-radius: 12px;
  padding: 14px 18px;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  background: linear-gradient(135deg, var(--accent-primary), color-mix(in srgb, var(--accent-primary) 75%, #6366f1));
  box-shadow: 0 12px 28px color-mix(in srgb, var(--accent-primary) 40%, transparent);
}
.plan-upgrade-dismiss {
  position: relative;
  z-index: 1;
  margin-top: 12px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-size: 13px;
  cursor: pointer;
}
.plan-upgrade-dismiss:hover {
  color: var(--text-primary);
}
</style>
